import { AxiosResponse } from 'axios';

import { loginApi } from '../setup/setupApi';
import { defaultUsersParams } from './defaultValues';
import {
  BanUserData,
  Group,
  User,
  UserEmailData,
  UserEmailResponse,
  UserGroupsData,
  UserLoginHistoryRecord,
  UserPhoneData,
  UsersData,
  UserServerCustomIdData,
  UserSocialProfile,
  UsersRequestParams
} from './types';

// Users requests

export const getUsers = (projectId: string, params?: UsersRequestParams): Promise<UsersData> =>
  loginApi
    .get(
      `projects/${projectId}/users/search${params?.user_groups ? `?${params.user_groups}` : ''}`,
      {
        params: { ...defaultUsersParams, ...params, user_groups: null }
      }
    )
    .then((res) => res.data);

export const getUser = (projectId: string, userId: string): Promise<User> =>
  loginApi.get(`projects/${projectId}/users/search/by_id/${userId}`).then((res) => res.data);

export const banUser = (projectId: string, userId: string, values: BanUserData): Promise<boolean> =>
  loginApi
    .post<BanUserData, AxiosResponse<boolean>>(`projects/${projectId}/users/${userId}/ban`, values)
    .then((res) => res.data);

export const unbanUser = (projectId: string, userId: string): Promise<boolean> =>
  loginApi.delete(`projects/${projectId}/users/${userId}/ban`).then((res) => res.data);

export const deleteUser = (userId: string, projectId: string): Promise<boolean> => {
  return loginApi.delete(`users/${userId}`, {
    params: { projectId }
  });
};

export const getUserLoginHistory = (
  projectId: string,
  userId: string
): Promise<UserLoginHistoryRecord[]> =>
  loginApi.get(`projects/${projectId}/users/${userId}/history`).then((res) => res.data);

export const setUserEmail = (
  projectId: string,
  userId: string,
  values: UserEmailData
): Promise<UserEmailResponse> => {
  const { Authorization } = loginApi?.defaults?.headers?.common as unknown as {
    Authorization: string;
  };

  return loginApi
    .post<UserEmailData, AxiosResponse<UserEmailResponse>>(
      `projects/${projectId}/users/${userId}/emails`,
      values,
      {
        headers: {
          Authorization: 'undefined',
          'X-SERVER-AUTHORIZATION': Authorization
        }
      }
    )
    .then((res) => res.data);
};

export const setServerCustomId = (
  projectId: string,
  userId: string,
  value: UserServerCustomIdData
): Promise<unknown> =>
  loginApi
    .put(`projects/${projectId}/users/${userId}/server_custom_id`, value)
    .then((res) => res.data);

export const setUserPhone = (
  projectId: string,
  userId: string,
  value: UserPhoneData
): Promise<unknown> =>
  loginApi.post(`projects/${projectId}/users/${userId}/phone`, value).then((res) => res.data);

export const resetEmailConfirmation = (
  projectId: string,
  userId: string
): Promise<UserEmailResponse> =>
  loginApi
    .post<UserEmailData, AxiosResponse<UserEmailResponse>>(
      `projects/${projectId}/users/${userId}/resend_confirm_email`
    )
    .then((res) => res.data);

export const resetPassword = (projectId: string, userId: string): Promise<boolean> =>
  loginApi.post(`projects/${projectId}/users/${userId}/reset_password`).then((res) => res.data);

export const getUserGroups = (projectId: string): Promise<Group[]> =>
  loginApi.get(`projects/${projectId}/user_groups`).then((res) => res.data);

export const updateUserGroups = (
  projectId: string,
  userId: string,
  values: UserGroupsData
): Promise<Group[]> =>
  loginApi
    .patch<UserGroupsData, AxiosResponse<Group[]>>(
      `projects/${projectId}/users/${userId}/user_groups`,
      values
    )
    .then((res) => res.data);

export const exportUsers = (projectId: string, email: string): Promise<unknown> =>
  loginApi.post(`projects/${projectId}/export_users`, { email }).then((res) => res.data);

export const getUserSocialProfile = (
  projectId: string,
  userId: string
): Promise<UserSocialProfile[]> =>
  loginApi.get(`projects/${projectId}/users/${userId}/social_profile`).then((res) => res.data);

export const deleteUserSocialProfile = (
  projectId: string,
  userId: string,
  providerName: string
): Promise<void> =>
  loginApi
    .delete(
      `projects/${projectId}/users/${userId}/social_profile/${encodeURIComponent(providerName)}`
    )
    .then(() => undefined);
